import 'twin.macro';
import React, { useCallback, useState } from 'react';
import SelectGif from '@teetsh/app/src/containers/EmploiDuTemps/EDT/Sidebar/Select.gif';
import { EmploiDuTemps } from '@teetsh/app/src/types/edt';
import { Classroom } from '@teetsh/app/src/types/classroom';
import SimpleModal from '@teetsh/app/src/components/SimpleModal';
import { useDownloadEDT } from '@teetsh/app/src/queries/edt/useDownloadEDT';
import Drawer from '@teetsh/app/src/components/Drawer';
import { EDTSendToCMSModal } from '@teetsh/app/src/containers/EmploiDuTemps/EDT/Sidebar/EDTSendToCMSModal';
import { useTranslation } from 'react-i18next';
import { Body } from '@teetsh/app/src/containers/EmploiDuTemps/EDT/Sidebar/Body';
import { DrawerView } from '@teetsh/app/src/containers/EmploiDuTemps/EDT/Sidebar/types';

interface Props {
  displayedClassrooms: string[];
  classrooms: Classroom[];
  emploiDuTemps: EmploiDuTemps;
  onChangeDisplayedClassrooms: (v: string[]) => void;
  externalDrawerView?: DrawerView;
  onExternalDrawerViewChange?: (view: DrawerView) => void;
}

export function SidebarComponent(props: Props) {
  const {
    emploiDuTemps,
    displayedClassrooms,
    classrooms,
    onChangeDisplayedClassrooms,
    externalDrawerView,
    onExternalDrawerViewChange,
  } = props;
  const [internalDrawerView, setInternalDrawerView] = useState<
    DrawerView | undefined
  >();

  // Use external drawer view if provided, otherwise use internal state
  const drawerView =
    externalDrawerView !== undefined ? externalDrawerView : internalDrawerView;
  const setDrawerView = onExternalDrawerViewChange || setInternalDrawerView;
  const { t } = useTranslation(['emploiDuTemps', 'common']);
  const [isEmptyEDTModalOpen, setIsEmptyEDTModalOpen] =
    useState<boolean>(false);

  const { download: downloadEDTPDF, isDownloading } = useDownloadEDT();
  const downloadPDF = useCallback(() => {
    const { events } = emploiDuTemps;
    if ((events === undefined || events.length === 0) && !isEmptyEDTModalOpen) {
      return setIsEmptyEDTModalOpen(true);
    }

    return downloadEDTPDF({
      classrooms: displayedClassrooms,
      edtName: emploiDuTemps.name,
      edtId: emploiDuTemps.id,
      callback: () => setIsEmptyEDTModalOpen(false),
    });
  }, [
    emploiDuTemps.name,
    emploiDuTemps.id,
    displayedClassrooms.join(''),
    JSON.stringify(emploiDuTemps),
    isEmptyEDTModalOpen,
  ]);

  return (
    <>
      <SimpleModal
        isOpen={isEmptyEDTModalOpen}
        title={t('emploiDuTemps:sidebar.emptyEDTModal.title')}
        intent="primary"
        validateTitle={t('emploiDuTemps:sidebar.emptyEDTModal.submitLabel')}
        validateAction={downloadPDF}
        isLoading={isDownloading}
        closeModalFct={() => setIsEmptyEDTModalOpen(false)}
      >
        <div tw="text-base space-y-6">
          <div>{t('emploiDuTemps:sidebar.emptyEDTModal.description1')}</div>
          <div>{t('emploiDuTemps:sidebar.emptyEDTModal.description2')}</div>
          <div tw="flex justify-center">
            <img src={SelectGif} alt="select matiere" />
          </div>
        </div>
      </SimpleModal>
      <EDTSendToCMSModal />
      <Drawer
        position="right"
        onClose={() => setDrawerView(undefined as any as DrawerView)}
        isOpen={!!drawerView}
        size="lg"
        title={
          drawerView === 'settings'
            ? t('emploiDuTemps:sidebar.settings')
            : t(
                `emploiDuTemps:sidebar.drawerTitles.${
                  drawerView || 'settings'
                }`,
              )
        }
      >
        <div tw="overflow-y-auto h-full px-4 py-6">
          <Body
            emploiDuTemps={emploiDuTemps}
            onChangeDisplayedClassrooms={onChangeDisplayedClassrooms}
            classrooms={classrooms}
            displayedClassrooms={displayedClassrooms}
            drawerView={drawerView}
          />
        </div>
      </Drawer>
    </>
  );
}
